import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedAmounts = [100, 500, 1000, 2500, 5000, 10000];

export default function BharatInitiativePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    amount: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseInt(formData.amount) < 100) {
      toast.error("Minimum contribution amount is ₹100");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contributions`, {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        amount: parseInt(formData.amount),
        program_id: "bharat-initiative",
        dynamic_field: "Initiative",
        dynamic_value: "Bharat Climate Contribution"
      });
      
      toast.success("Thank you for your contribution to India's climate future!");
      
      setTimeout(() => {
        navigate(`/payment-success?type=contribution&id=${response.data.id}&certificate=${response.data.certificate_id}`);
      }, 1500);
    } catch (error) {
      console.error("Contribution error:", error);
      toast.error("Contribution failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="bharat-initiative-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-orange-500 via-white to-green-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Heart className="h-4 w-4" />
            Bharat Climate Contribution Initiative
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            One Credit for the Planet
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Enable individuals and institutions to participate in nation-scale climate action.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Info */}
            <div>
              <h2 className="font-montserrat text-3xl font-bold text-slate-900 mb-6">
                About the Initiative
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  The Bharat Climate Contribution Initiative enables individuals and institutions 
                  to participate in nation-scale climate action.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Contributions made through this initiative are directed towards supporting the 
                  offset of carbon emissions from government-operated and public sector activities, 
                  including infrastructure, utilities and essential services.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  By participating, you are contributing to India's transition towards a more 
                  sustainable and responsible future.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                  <p className="font-semibold text-orange-900">
                    This is not just a contribution — it is a step towards national climate responsibility.
                  </p>
                </div>
              </div>

              {/* India Stats */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 text-center">
                  <p className="text-3xl font-bold text-orange-700">2070</p>
                  <p className="text-sm text-orange-600">Net Zero Target</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 text-center">
                  <p className="text-3xl font-bold text-green-700">45%</p>
                  <p className="text-sm text-green-600">Emissions Reduction by 2030</p>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <Card className="sticky top-24">
              <CardContent className="p-6 lg:p-8">
                <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-6">
                  Contribute to Bharat Climate Initiative
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      data-testid="bharat-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      required
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="Enter mobile number"
                      data-testid="bharat-mobile"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email address"
                      data-testid="bharat-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Contribution Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      required
                      min="100"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Enter amount (min ₹100)"
                      data-testid="bharat-amount"
                    />
                    <div className="flex flex-wrap gap-2 mt-3">
                      {suggestedAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                          className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                            formData.amount === amount.toString()
                              ? "bg-orange-600 text-white border-orange-600"
                              : "border-slate-200 hover:border-orange-500 hover:text-orange-600"
                          }`}
                        >
                          ₹{amount.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {formData.amount && parseInt(formData.amount) >= 100 && (
                    <div className="bg-gradient-to-r from-orange-50 to-green-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-600">Your contribution will offset</p>
                      <p className="text-3xl font-bold text-forest-900">
                        {((parseInt(formData.amount) / 100) * 10).toLocaleString()} kg CO₂
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-600 to-green-600 hover:from-orange-700 hover:to-green-700"
                    size="lg"
                    disabled={submitting}
                    data-testid="bharat-submit"
                  >
                    {submitting ? "Processing..." : "Contribute to Bharat Climate Initiative"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    You will receive a certificate of contribution after payment.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
